import { useState, useEffect, useRef } from "react";
import { formatTimestamp, sortByTimestampDescending } from "../shared/helper";
import type { AdminMessage as Message } from "../types/admin";
import { API_BASE_URL } from "../utils/api";

interface ViewGameMessagesProps {
  gameId: string;
  onClose: () => void;
  refreshKey?: number;
  messagesData?: Message[];
}

export default function ViewGameMessages({ gameId, onClose, refreshKey, messagesData }: ViewGameMessagesProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  const previousMessagesDataRef = useRef<Message[]>([]);
  const messagesRef = useRef<Message[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (messagesData) {
      // Check for new messages that weren't in the current messages state
      const currentMessageIds = new Set(messagesRef.current.map(m => m.message_id));
      const newMessages = messagesData.filter(m => !currentMessageIds.has(m.message_id));
      
      // If there are new messages and we have existing messages displayed, add new ones to the top
      if (newMessages.length > 0 && messagesRef.current.length > 0) {
        // Get existing messages (excluding the new ones) and update any that changed
        const existingMessages = messagesRef.current
          .filter(m => messagesData.some(newMsg => newMsg.message_id === m.message_id))
          .map(existingMessage => {
            const updated = messagesData.find(m => m.message_id === existingMessage.message_id);
            return updated || existingMessage;
          });
        
        // Sort existing messages by timestamp
        const sortedExisting = sortByTimestampDescending(existingMessages, (msg) => msg.timestamp);
        
        // New messages go to the top, then existing messages
        setMessages([...newMessages, ...sortedExisting]);
      } else {
        // Initial load or no new messages - just sort all by timestamp
        const sorted = sortByTimestampDescending(messagesData, (msg) => msg.timestamp);
        setMessages(sorted);
      }
      
      // Update refs
      previousMessagesDataRef.current = messagesData;
      messagesData.forEach(m => seenMessageIdsRef.current.add(m.message_id));
      
      setIsLoading(false);
      setError("");
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError("");
    fetch(`${API_BASE_URL}/messages/by-game-id?game_id=${gameId}`)
      .then(res => {
        if (!res.ok) {
          // Endpoint should return 200 with empty array, but handle 404 as empty for legacy support
          if (res.status === 404) {
            return [];
          }
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to fetch messages");
          });
        }
        return res.json();
      })
      .then((data: Message[]) => {
        if (cancelled) return;
        // Handle both array and non-array responses
        const messagesArray = Array.isArray(data) ? data : [];
        const sorted = sortByTimestampDescending(messagesArray, (msg) => msg.timestamp);
        setMessages(sorted);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to fetch messages");
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [messagesData, gameId, refreshKey]);

  // Build aligned team → players map to render agencies on one row and spaceships on another
  const buildTeamPlayers = () => {
    const teamOrder: string[] = [];
    const teamToPlayers: Record<string, {
      agency?: { id: string; name: string };
      spaceship?: { id: string; name: string };
    }> = {};

    const consider = (teamName: string | null, playerId: string | null, playerName: string | null, role: string | null) => {
      if (!teamName || !playerId || !playerName || !role) return;
      if (!teamToPlayers[teamName]) {
        teamToPlayers[teamName] = {};
        teamOrder.push(teamName);
      }
      const roleLower = role.toLowerCase();
      if (roleLower.includes("agency")) {
        teamToPlayers[teamName].agency = { id: playerId, name: playerName };
      } else if (roleLower.includes("spaceship")) {
        teamToPlayers[teamName].spaceship = { id: playerId, name: playerName };
      }
    };

    messages.forEach(msg => {
      consider(msg.sending_team_name, msg.sending_player_id, msg.sending_player_name, msg.sending_player_role);
      consider(msg.receiving_team_name, msg.receiving_player_id, msg.receiving_player_name, msg.receiving_player_role);
    });

    return { teamOrder, teamToPlayers };
  };

  // Filter messages based on selected player
  const filteredMessages = selectedPlayerId
    ? messages.filter(msg => 
        msg.sending_player_id === selectedPlayerId || 
        msg.receiving_player_id === selectedPlayerId
      )
    : messages;

  const { teamOrder, teamToPlayers } = buildTeamPlayers();

  return (
    <div>
      {!isLoading && !error && messages.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={() => setSelectedPlayerId(null)}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: "pointer",
              backgroundColor: selectedPlayerId === null ? "#007bff" : "#e9ecef",
              color: selectedPlayerId === null ? "white" : "#212529",
              border: "none",
              borderRadius: "4px"
            }}
          >
            All Messages
          </button>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            {teamOrder.map((team) => {
              const p = teamToPlayers[team]?.agency;
              if (!p) return null;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlayerId(p.id)}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    backgroundColor: selectedPlayerId === p.id ? "#007bff" : "#e9ecef",
                    color: selectedPlayerId === p.id ? "white" : "#212529",
                    border: "none",
                    borderRadius: "4px"
                  }}
                >
                  {p.name}
                </button>
              );
            })}
            {teamOrder.map((team) => {
              const p = teamToPlayers[team]?.spaceship;
              if (!p) return null;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlayerId(p.id)}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    backgroundColor: selectedPlayerId === p.id ? "#007bff" : "#e9ecef",
                    color: selectedPlayerId === p.id ? "white" : "#212529",
                    border: "none",
                    borderRadius: "4px"
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && (
        <div style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}>Loading messages...</div>
      )}

      {error && (
        <div style={{ padding: "1rem", color: "#dc3545", fontSize: "0.875rem" }}>{error}</div>
      )}

      {!isLoading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.875rem"
          }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Turn</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>From</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>To</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Original Message</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Final Message</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Sender Comm lvl</th>
                <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "bold", color: "#212529" }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {messages.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    No messages found
                  </td>
                </tr>
              ) : selectedPlayerId && filteredMessages.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "2rem", textAlign: "center", color: "#6c757d", fontStyle: "italic" }}>
                    No messages found for selected player
                  </td>
                </tr>
              ) : (
                filteredMessages.map((message) => {
                  const isAdminMessage = message.is_admin_message || message.admin_id || !message.sending_player_id;
                  const rowStyle = isAdminMessage
                    ? { borderBottom: "1px solid #dee2e6", backgroundColor: "#e3f2fd" }  // Light blue background
                    : { borderBottom: "1px solid #dee2e6" };
                  
                  return (
                    <tr key={message.message_id} style={rowStyle}>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>{message.turn_number}</td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>
                        {isAdminMessage ? (
                          <strong style={{ color: "#1976d2" }}>{message.admin_name || "Admin"}</strong>
                        ) : (
                          message.sending_player_name || message.sending_player_id
                        )}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>
                        {message.receiving_player_name || message.receiving_player_id}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "200px", wordWrap: "break-word" }}>
                        {message.original_message}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529", maxWidth: "200px", wordWrap: "break-word" }}>
                        {message.final_message}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#212529" }}>
                        {message.comm_lvl_of_sender !== null ? message.comm_lvl_of_sender : "-"}
                      </td>
                      <td style={{ padding: "0.75rem", color: "#6c757d", fontSize: "0.8rem" }}>
                        {formatTimestamp(message.timestamp)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

